import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Chip,
  IconButton, Tooltip, Alert, CircularProgress, Autocomplete, Grid,
  InputAdornment, Checkbox, RadioGroup, Radio, FormControlLabel, FormLabel,
  Collapse, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PaymentIcon from '@mui/icons-material/Payment';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DoneAllIcon from '@mui/icons-material/DoneAll';

const API = '/api/contabilidad';

const fmtFecha = s => {
  if (!s) return '';
  const [y, m, d] = String(s).substring(0, 10).split('-');
  return (y && m && d) ? `${d}/${m}/${y}` : String(s).substring(0, 10);
};

const TIPOS_DOC_VENTA = [
  { code: '01', label: '01 — Factura' },
  { code: '03', label: '03 — Boleta de Venta' },
  { code: '07', label: '07 — Nota de Crédito' },
  { code: '08', label: '08 — Nota de Débito' },
  { code: '12', label: '12 — Ticket (máq. registradora)' },
  { code: '14', label: '14 — Recibo Servicios Públicos' },
];

const EMPTY_FORM = {
  tipo_documento: '01', serie: '', numero: '',
  fecha_emision: '', fecha_vencimiento: '',
  moneda: 'PEN', tipo_cambio: '1.0000',
  tipo_doc_identidad: '6', num_documento: '', razon_social: '',
  base_gravada: '', igv: '', base_exonerada: '', base_inafecta: '',
  isc: '', otros_tributos: '', total: '', glosa: '', destino_operacion: 'GRAVADA', centro_costo_id: '',
  // Datos SUNAT adicionales (opcionales) — doc. de referencia para NC/ND, detracción,
  // retención, percepción.
  doc_ref_tipo: '', doc_ref_serie: '', doc_ref_numero: '',
  tiene_detraccion: false, det_fecha: '', det_numero: '', det_tasa: '', det_monto: '', det_tipo: '',
  tiene_retencion: false, ret_codigo_tasa: '', ret_numero: '',
  tiene_percepcion: false, per_codigo_tasa: '', per_numero: '',
};

const IGV_RATE = 0.18;

// Mismo catálogo que ya usa Tesorería (frontend/src/pages/Tesoreria.js) — se reutiliza
// tal cual para que ambos módulos hablen el mismo idioma.
const MEDIOS_PAGO = ['EFECTIVO', 'TRANSFERENCIA', 'TARJETA', 'CHEQUE', 'YAPE', 'PLIN', 'OTRO'];

function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function chipEstado(estado) {
  const map = { PENDIENTE: 'warning', CANCELADO: 'success', ANULADO: 'error' };
  return <Chip label={estado} color={map[estado] || 'default'} size="small" />;
}

export default function RegistroVentas() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [periodos, setPeriodos] = useState([]);
  const [periodoSel, setPeriodoSel] = useState('');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [igvManual, setIgvManual] = useState(false);
  const [showAdicionales, setShowAdicionales] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [buscar, setBuscar] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [cobDialog, setCobDialog] = useState(false);
  const [cobDoc, setCobDoc] = useState(null);
  const [cobForm, setCobForm] = useState({ fecha_cancelacion: '', cuenta_cobpag_id: '', monto_cancelado: '', glosa: '', medio_pago: 'EFECTIVO', pago_referencia: '' });
  const [cuentasCaja, setCuentasCaja] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [snack, setSnack] = useState('');
  const [consultandoDoc, setConsultandoDoc] = useState(false);
  const [consultaErr, setConsultaErr] = useState('');

  // Importación por plantilla Excel
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importErr, setImportErr] = useState('');

  // Selección múltiple + cancelación masiva
  const [seleccionados, setSeleccionados] = useState([]);
  const [cancelMasivoOpen, setCancelMasivoOpen] = useState(false);
  const [cancelMasivoForm, setCancelMasivoForm] = useState({
    modo: 'individual', fecha_cancelacion: '', cuenta_cobpag_id: '',
    medio_pago: 'EFECTIVO', pago_referencia: '', usar_fecha_emision: false,
  });
  const [cancelMasivoErr, setCancelMasivoErr] = useState('');
  const [cancelandoMasivo, setCancelandoMasivo] = useState(false);

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    axios.get(`${API}/periodos?empresa_id=${empresaId}`).then(r => {
      const abiertos = r.data.filter(p => p.estado === 'ABIERTO').sort((a, b) => b.periodo.localeCompare(a.periodo));
      setPeriodos(r.data);
      if (abiertos.length > 0 && !periodoSel) setPeriodoSel(abiertos[0].periodo);
    }).catch(() => {});
    axios.get(`${API}/plan?empresa_id=${empresaId}&solo_hojas=1&buscar=10`).then(r => setCuentasCaja(r.data)).catch(() => {});
    axios.get(`${API}/centros-costo`, { params: { empresa_id: empresaId } })
      .then(r => setCentrosCosto(Array.isArray(r.data) ? r.data.filter(c => c.activo) : []))
      .catch(() => {});
  }, [empresaId]);

  const empresaSel = empresas.find(e => String(e.id) === String(empresaId));
  const usaCentroCosto = !!empresaSel?.usa_centro_costo;

  const fetchDocs = useCallback(async () => {
    if (!empresaId || !periodoSel) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/doc-registro`, {
        params: { empresa_id: empresaId, periodo: periodoSel, tipo_operacion: 'VENTA', estado: filtroEstado || undefined, buscar: buscar || undefined }
      });
      setDocs(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId, periodoSel, filtroEstado, buscar]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  function calcularAutomatico(f, manualOverride) {
    const base = parseFloat(f.base_gravada) || 0;
    const igvCalc = +(base * IGV_RATE).toFixed(2);
    const exo = parseFloat(f.base_exonerada) || 0;
    const ina = parseFloat(f.base_inafecta) || 0;
    const isc = parseFloat(f.isc) || 0;
    const otros = parseFloat(f.otros_tributos) || 0;
    const manual = manualOverride !== undefined ? manualOverride : igvManual;
    const igv = manual ? (parseFloat(f.igv) || 0) : igvCalc;
    const total = +(base + igv + exo + ina + isc + otros).toFixed(2);
    return { ...f, igv: igv.toFixed(2), total: total.toFixed(2) };
  }

  function handleChange(e) {
    const { name, value } = e.target;
    let upd = { ...form, [name]: value };
    if (['base_gravada', 'igv', 'base_exonerada', 'base_inafecta', 'isc', 'otros_tributos'].includes(name)) {
      upd = calcularAutomatico(upd);
    }
    setForm(upd);
    if (name === 'fecha_emision' && upd.moneda === 'USD') autocompletarTcVenta(value);
    if (name === 'moneda' && value === 'USD' && upd.fecha_emision) autocompletarTcVenta(upd.fecha_emision);
  }

  // Ventas usa TC venta (regla SUNAT: activos/ingresos al TC venta). Busca primero en la
  // tabla ya sincronizada; si no hay dato para esa fecha, consulta la API de SUNAT en vivo
  // (mismo patrón de 2 pasos que ya usa Factura.js en el ERP principal). El campo queda
  // editable igual, por si el contador necesita corregirlo.
  async function autocompletarTcVenta(fecha) {
    if (!fecha) return;
    try {
      const resBD = await axios.get('/api/tipo-cambio');
      const tc = resBD.data.find(x => x.fecha && x.fecha.substring(0, 10) === fecha);
      if (tc && tc.venta) { setForm(f => ({ ...f, tipo_cambio: tc.venta })); return; }
    } catch {}
    try {
      const res = await axios.get(`/api/tipo-cambio/sunat?fecha=${fecha}`);
      if (res.data && res.data.venta) { setForm(f => ({ ...f, tipo_cambio: res.data.venta })); }
    } catch {}
  }

  // NC/ND: hereda moneda y TC del documento que referencia (igual que NotaCredito.js en el
  // ERP principal) — para que la nota cancele exactamente en la misma moneda/valor, sin una
  // diferencia de cambio artificial solo por usar la fecha de la propia nota.
  async function autocompletarDesdeReferencia(serie, numero) {
    if (!serie || !numero || !empresaId) return;
    try {
      const r = await axios.get(`${API}/doc-registro/referencia/buscar`, {
        params: { empresa_id: empresaId, tipo_operacion: 'VENTA', serie, numero }
      });
      if (r.data) {
        setForm(f => ({ ...f, moneda: r.data.moneda || f.moneda, tipo_cambio: r.data.tipo_cambio || f.tipo_cambio }));
      }
    } catch {}
  }

  function handleToggleIgvManual(checked) {
    setIgvManual(checked);
    setForm(f => calcularAutomatico(f, checked));
  }

  async function consultarDocumento() {
    const doc = form.num_documento.trim();
    if (!doc) return;
    setConsultandoDoc(true);
    setConsultaErr('');
    try {
      if (form.tipo_doc_identidad === '6') {
        const r = await axios.get(`/api/consulta-peru/ruc/${doc}`);
        if (r.data?.razonSocial || r.data?.nombre) {
          setForm(f => ({ ...f, razon_social: r.data.razonSocial || r.data.nombre || '' }));
        } else {
          setConsultaErr('RUC no encontrado en SUNAT');
        }
      } else if (form.tipo_doc_identidad === '1') {
        const r = await axios.get(`/api/consulta-peru/dni/${doc}`);
        if (r.data?.nombre || r.data?.nombres) {
          const nombre = r.data.nombre || `${r.data.nombres || ''} ${r.data.apellidoPaterno || ''} ${r.data.apellidoMaterno || ''}`.trim();
          setForm(f => ({ ...f, razon_social: nombre }));
        } else {
          setConsultaErr('DNI no encontrado en RENIEC');
        }
      }
    } catch {
      setConsultaErr('Error al consultar. Verifique el número.');
    } finally {
      setConsultandoDoc(false);
    }
  }

  async function handleSave() {
    setSaveErr('');
    try {
      await axios.post(`${API}/doc-registro`, {
        ...form,
        empresa_id: empresaId,
        periodo: periodoSel,
        tipo_operacion: 'VENTA',
        base_gravada: parseFloat(form.base_gravada) || 0,
        igv: parseFloat(form.igv) || 0,
        base_exonerada: parseFloat(form.base_exonerada) || 0,
        base_inafecta: parseFloat(form.base_inafecta) || 0,
        isc: parseFloat(form.isc) || 0,
        otros_tributos: parseFloat(form.otros_tributos) || 0,
        total: parseFloat(form.total) || 0,
        tipo_cambio: parseFloat(form.tipo_cambio) || 1,
      });
      setOpen(false);
      setSnack('Documento registrado y asiento generado');
      fetchDocs();
    } catch (e) {
      setSaveErr(e.response?.data?.error || 'Error al guardar');
    }
  }

  async function handleAnular(id) {
    if (!window.confirm('¿Anular este documento? Se anularán sus asientos contables.')) return;
    try {
      await axios.put(`${API}/doc-registro/${id}/anular`);
      setSnack('Documento anulado');
      fetchDocs();
    } catch (e) {
      alert(e.response?.data?.error || 'Error');
    }
  }

  async function handleCobro() {
    if (!cobForm.fecha_cancelacion || !cobForm.cuenta_cobpag_id) {
      alert('Complete fecha y cuenta de cobro');
      return;
    }
    try {
      await axios.put(`${API}/doc-registro/${cobDoc.id}/cancelar`, {
        ...cobForm,
        monto_cancelado: parseFloat(cobForm.monto_cancelado) || parseFloat(cobDoc.total),
      });
      setCobDialog(false);
      setSnack('Cobro registrado y asiento generado');
      fetchDocs();
    } catch (e) {
      alert(e.response?.data?.error || 'Error');
    }
  }

  async function handleDescargarPlantilla() {
    try {
      const r = await axios.get(`${API}/doc-registro/plantilla`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'Plantilla_Ventas_Compras.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Error al descargar la plantilla'); }
  }

  async function handleImportarPreview(file) {
    setImporting(true); setImportErr(''); setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      fd.append('empresa_id', empresaId);
      fd.append('generar_asientos', '1');
      fd.append('solo_preview', '1');
      const r = await axios.post(`${API}/doc-registro/importar`, fd);
      setImportPreview(r.data);
    } catch (e) { setImportErr(e.response?.data?.error || 'Error al leer el archivo'); }
    setImporting(false);
  }

  async function handleImportarConfirmar() {
    if (!importFile) return;
    setImporting(true); setImportErr('');
    try {
      const fd = new FormData();
      fd.append('archivo', importFile);
      fd.append('empresa_id', empresaId);
      fd.append('generar_asientos', '1');
      fd.append('solo_preview', '0');
      const r = await axios.post(`${API}/doc-registro/importar`, fd);
      setSnack(`${r.data.importados} documento(s) importado(s)` + (r.data.errores?.length ? ` — ${r.data.errores.length} advertencia(s)` : ''));
      setImportOpen(false); setImportFile(null); setImportPreview(null);
      fetchDocs();
    } catch (e) { setImportErr(e.response?.data?.error || 'Error al importar'); }
    setImporting(false);
  }

  function toggleSeleccion(id) {
    setSeleccionados(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }

  const pendientesDocs = docs.filter(d => d.estado === 'PENDIENTE');
  const seleccionadosDocs = docs.filter(d => seleccionados.includes(d.id));
  const totalSeleccionado = seleccionadosDocs.reduce((s, d) => s + parseFloat(d.total || 0), 0);

  async function handleCancelarMasivo() {
    if (!cancelMasivoForm.usar_fecha_emision && !cancelMasivoForm.fecha_cancelacion) {
      setCancelMasivoErr('Complete la fecha de cobro (o marque "usar fecha de emisión")'); return;
    }
    if (!cancelMasivoForm.cuenta_cobpag_id) {
      setCancelMasivoErr('Seleccione la cuenta de cobro'); return;
    }
    setCancelandoMasivo(true); setCancelMasivoErr('');
    try {
      const r = await axios.put(`${API}/doc-registro/cancelar-masivo`, {
        ids: seleccionados, modo: cancelMasivoForm.modo,
        fecha_cancelacion: cancelMasivoForm.fecha_cancelacion,
        cuenta_cobpag_id: cancelMasivoForm.cuenta_cobpag_id,
        medio_pago: cancelMasivoForm.medio_pago,
        pago_referencia: cancelMasivoForm.pago_referencia,
        usar_fecha_emision: cancelMasivoForm.usar_fecha_emision,
      });
      setSnack(`${r.data.cancelados} documento(s) cancelado(s) en ${r.data.modo === 'agrupado' ? '1 asiento agrupado' : `${r.data.cancelados} asiento(s) individual(es)`}`);
      setCancelMasivoOpen(false); setSeleccionados([]);
      fetchDocs();
    } catch (e) { setCancelMasivoErr(e.response?.data?.error || 'Error al cancelar'); }
    setCancelandoMasivo(false);
  }

  const totalBase = docs.filter(d => d.estado !== 'ANULADO').reduce((s, d) => s + parseFloat(d.base_gravada || 0), 0);
  const totalIgv = docs.filter(d => d.estado !== 'ANULADO').reduce((s, d) => s + parseFloat(d.igv || 0), 0);
  const totalMonto = docs.filter(d => d.estado !== 'ANULADO').reduce((s, d) => s + parseFloat(d.total || 0), 0);

  const puedeConsultar = (form.tipo_doc_identidad === '6' && form.num_documento.length === 11) ||
                         (form.tipo_doc_identidad === '1' && form.num_documento.length === 8);

  return (
    <Box sx={{ width: '100%', maxWidth: 1600, mx: 'auto', mt: 1 }}>
      <Typography variant="h5" fontWeight="bold" mb={2}>Registro de Ventas</Typography>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Empresa" value={empresaId} onChange={e => { setEmpresaId(e.target.value); setPeriodoSel(''); }} sx={{ minWidth: '24em' }}>
              {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Período" value={periodoSel} onChange={e => setPeriodoSel(e.target.value)} disabled={!empresaId} sx={{ minWidth: '20em' }}>
              {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{p.nombre || p.periodo}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Estado" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} sx={{ minWidth: '20em' }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="PENDIENTE">Pendiente</MenuItem>
              <MenuItem value="CANCELADO">Cancelado</MenuItem>
              <MenuItem value="ANULADO">Anulado</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField fullWidth size="small" placeholder="Buscar RUC, razón social, serie..." value={buscar} onChange={e => setBuscar(e.target.value)} sx={{ minWidth: '26em' }}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }} />
          </Grid>
          <Grid item xs={12} sm={12} md="auto" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Descargar plantilla Excel para importar ventas">
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDescargarPlantilla}>
                Plantilla
              </Button>
            </Tooltip>
            <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} disabled={!empresaId}
              onClick={() => { setImportFile(null); setImportPreview(null); setImportErr(''); setImportOpen(true); }}>
              Importar
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} disabled={!empresaId || !periodoSel}
              onClick={() => { setForm({ ...EMPTY_FORM, fecha_emision: new Date().toISOString().substring(0,10) }); setIgvManual(false); setSaveErr(''); setConsultaErr(''); setOpen(true); }}>
              Nuevo
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {seleccionados.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#FFF8E1' }}>
          <Typography variant="body2">
            <b>{seleccionados.length}</b> documento(s) seleccionado(s) — Total: <b>S/ {fmt(totalSeleccionado)}</b>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={() => setSeleccionados([])}>Limpiar selección</Button>
            <Button size="small" variant="contained" color="success" startIcon={<DoneAllIcon />}
              onClick={() => { setCancelMasivoForm({ modo: 'individual', fecha_cancelacion: new Date().toISOString().substring(0,10), cuenta_cobpag_id: '' }); setCancelMasivoErr(''); setCancelMasivoOpen(true); }}>
              Cancelar seleccionados
            </Button>
          </Box>
        </Paper>
      )}

      {/* Tabla */}
      {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} /> : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox size="small" indeterminate={seleccionados.length > 0 && seleccionados.length < pendientesDocs.length}
                    checked={pendientesDocs.length > 0 && seleccionados.length === pendientesDocs.length}
                    disabled={pendientesDocs.length === 0}
                    onChange={() => setSeleccionados(seleccionados.length === pendientesDocs.length ? [] : pendientesDocs.map(d => d.id))} />
                </TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Serie-Número</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell align="right">Base Grav.</TableCell>
                <TableCell align="right">IGV</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Mon.</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map(d => (
                <TableRow key={d.id} hover sx={{ opacity: d.estado === 'ANULADO' ? 0.5 : 1 }}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={seleccionados.includes(d.id)} disabled={d.estado !== 'PENDIENTE'}
                      onChange={() => toggleSeleccion(d.id)} />
                  </TableCell>
                  <TableCell>{d.tipo_documento}</TableCell>
                  <TableCell>{d.serie}-{d.numero}</TableCell>
                  <TableCell>{fmtFecha(d.fecha_emision)}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.razon_social}</TableCell>
                  <TableCell align="right">{fmt(d.base_gravada)}</TableCell>
                  <TableCell align="right">{fmt(d.igv)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(d.total)}</TableCell>
                  <TableCell>{d.moneda || 'PEN'}</TableCell>
                  <TableCell>{chipEstado(d.estado)}</TableCell>
                  <TableCell>
                    {d.estado === 'PENDIENTE' && (
                      <>
                        <Tooltip title="Registrar cobro"><IconButton size="small" color="success" onClick={() => { setCobDoc(d); setCobForm({ fecha_cancelacion: new Date().toISOString().substring(0,10), cuenta_cobpag_id: '', monto_cancelado: d.total, glosa: '', medio_pago: 'EFECTIVO', pago_referencia: '' }); setCobDialog(true); }}><PaymentIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Anular"><IconButton size="small" color="error" onClick={() => handleAnular(d.id)}><BlockIcon fontSize="small" /></IconButton></Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {docs.length === 0 && (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay documentos en este período</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Totales */}
      {docs.length > 0 && (
        <Paper sx={{ mt: 2, p: 2, borderRadius: 2, display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <Box><Typography variant="caption" color="text.secondary">Base Gravada</Typography><Typography fontWeight="bold">S/ {fmt(totalBase)}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">IGV</Typography><Typography fontWeight="bold">S/ {fmt(totalIgv)}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">Total Ventas</Typography><Typography fontWeight="bold" color="primary.main">S/ {fmt(totalMonto)}</Typography></Box>
        </Paper>
      )}

      {snack && <Alert severity="success" onClose={() => setSnack('')} sx={{ mt: 2 }}>{snack}</Alert>}

      {/* ── Dialog Nuevo Documento ── */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle><Typography fontWeight="bold">Nuevo Documento de Venta</Typography></DialogTitle>
        <DialogContent>
          {saveErr && <Alert severity="error" sx={{ mb: 1 }}>{saveErr}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>

            {/* ── Fila 1: Tipo Documento | Serie | Número ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField select size="small" label="Tipo Documento" name="tipo_documento" value={form.tipo_documento} onChange={handleChange}
                sx={{ width: { xs: '100%', sm: '22em' } }}>
                {TIPOS_DOC_VENTA.map(t => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Serie" name="serie" value={form.serie} onChange={handleChange} placeholder="F001"
                sx={{ width: '10em' }} />
              <TextField size="small" label="Número" name="numero" value={form.numero} onChange={handleChange} placeholder="00000001"
                sx={{ flex: 1, minWidth: '8em' }} />
            </Box>

            {/* ── Fila 2: Fecha Emisión | Fecha Vencimiento | Moneda | T.C. ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField size="small" label="Fecha Emisión" name="fecha_emision" type="date" value={form.fecha_emision} onChange={handleChange}
                InputLabelProps={{ shrink: true }} sx={{ flex: 1, minWidth: '10em' }} />
              <TextField size="small" label="Fecha Vencimiento" name="fecha_vencimiento" type="date" value={form.fecha_vencimiento} onChange={handleChange}
                InputLabelProps={{ shrink: true }} sx={{ flex: 1, minWidth: '10em' }} />
              <TextField select size="small" label="Moneda" name="moneda" value={form.moneda} onChange={handleChange}
                sx={{ width: '9em' }}>
                <MenuItem value="PEN">S/ PEN</MenuItem>
                <MenuItem value="USD">$ USD</MenuItem>
              </TextField>
              <TextField size="small" label="T.C." name="tipo_cambio" value={form.tipo_cambio} onChange={handleChange}
                disabled={form.moneda === 'PEN'} sx={{ width: '7em' }} />
              {usaCentroCosto && (
                <TextField select size="small" label="Centro de Costo" name="centro_costo_id" value={form.centro_costo_id}
                  onChange={handleChange} sx={{ flex: 1, minWidth: '13em' }}>
                  <MenuItem value="">— Sin asignar —</MenuItem>
                  {centrosCosto.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.descripcion}</MenuItem>
                  ))}
                </TextField>
              )}
            </Box>

            {/* ── Label: Datos del Cliente ── */}
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ textAlign: 'center' }}>
              Datos del Cliente
            </Typography>

            {/* ── Fila 3: Tipo Doc Identidad | Nro Documento | Razón Social ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField select size="small" label="Tipo Doc. Identidad" name="tipo_doc_identidad"
                value={form.tipo_doc_identidad} onChange={handleChange}
                sx={{ width: { xs: '100%', sm: '10em' } }}>
                <MenuItem value="6">RUC</MenuItem>
                <MenuItem value="1">DNI</MenuItem>
                <MenuItem value="4">Carnet Ext.</MenuItem>
                <MenuItem value="0">Sin doc</MenuItem>
              </TextField>
              <TextField size="small" label="Nro. Documento" name="num_documento" value={form.num_documento} onChange={handleChange}
                onKeyDown={e => { if (e.key === 'Enter' && puedeConsultar) consultarDocumento(); }}
                sx={{ width: { xs: '100%', sm: '12em' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={form.tipo_doc_identidad === '6' ? 'Consultar SUNAT' : form.tipo_doc_identidad === '1' ? 'Consultar RENIEC' : 'Consultar'}>
                        <span>
                          <IconButton size="small" onClick={consultarDocumento}
                            disabled={consultandoDoc || !puedeConsultar}
                            sx={{ color: 'primary.main' }}>
                            {consultandoDoc ? <CircularProgress size={16} /> : <ManageSearchIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
                helperText={consultaErr ? <span style={{ color: 'red' }}>{consultaErr}</span> : (form.tipo_doc_identidad === '6' ? 'RUC 11 dígitos → SUNAT' : form.tipo_doc_identidad === '1' ? 'DNI 8 dígitos → RENIEC' : '')}
              />
              <TextField size="small" label="Razón Social / Nombre" name="razon_social"
                value={form.razon_social} onChange={handleChange} sx={{ flex: 1, minWidth: '26em' }} />
            </Box>

            {/* ── Label: Importes ── */}
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ textAlign: 'center' }}>
              Importes
            </Typography>

            {/* ── Fila 4: Base Gravada | IGV | Base Exonerada ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              <TextField size="small" label="Base Gravada" name="base_gravada" value={form.base_gravada}
                onChange={handleChange} type="number" inputProps={{ step: '0.01' }} sx={{ flex: 1, minWidth: '9em' }} />
              <TextField size="small" label={igvManual ? 'IGV' : 'IGV (automático 18%)'} name="igv" value={form.igv}
                onChange={handleChange} type="number" inputProps={{ step: '0.01' }} disabled={!igvManual}
                sx={{ flex: 1, minWidth: '9em' }} />
              <TextField size="small" label="Base Exonerada" name="base_exonerada" value={form.base_exonerada}
                onChange={handleChange} type="number" inputProps={{ step: '0.01' }} sx={{ flex: 1, minWidth: '9em' }} />
              <FormControlLabel sx={{ ml: 0 }} control={
                <Checkbox size="small" checked={igvManual} onChange={e => handleToggleIgvManual(e.target.checked)} />
              } label={<Typography variant="caption">Editar IGV manualmente</Typography>} />
              <FormControlLabel sx={{ ml: 0 }} control={
                <Checkbox size="small" checked={form.destino_operacion === 'EXPORTACION'}
                  onChange={e => setForm(f => ({ ...f, destino_operacion: e.target.checked ? 'EXPORTACION' : 'GRAVADA' }))} />
              } label={<Typography variant="caption">Operación de exportación (Registro de Ventas SUNAT)</Typography>} />
            </Box>

            {/* ── Fila 5: Base Inafecta | ISC | Otros Tributos ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField size="small" label="Base Inafecta" name="base_inafecta" value={form.base_inafecta}
                onChange={handleChange} type="number" inputProps={{ step: '0.01' }} sx={{ flex: 1, minWidth: '9em' }} />
              <TextField size="small" label="ISC" name="isc" value={form.isc}
                onChange={handleChange} type="number" inputProps={{ step: '0.01' }} sx={{ flex: 1, minWidth: '9em' }} />
              <TextField size="small" label="Otros Tributos" name="otros_tributos" value={form.otros_tributos}
                onChange={handleChange} type="number" inputProps={{ step: '0.01' }} sx={{ flex: 1, minWidth: '9em' }} />
            </Box>

            {/* ── Fila 6: Total | Glosa ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField size="small" label="Total" name="total" value={form.total}
                onChange={handleChange} type="number" inputProps={{ step: '0.01' }}
                InputProps={{ sx: { fontWeight: 'bold', bgcolor: '#f0f7ff' } }}
                sx={{ width: '11em' }} />
              <TextField size="small" label="Glosa (opcional)" name="glosa" value={form.glosa}
                onChange={handleChange} sx={{ flex: 1, minWidth: '14em' }} />
            </Box>

            {/* ── Datos SUNAT adicionales (opcional): NC/ND, detracción, retención, percepción ── */}
            <Box>
              <Button size="small" onClick={() => setShowAdicionales(v => !v)}
                endIcon={<ExpandMoreIcon sx={{ transform: showAdicionales ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
                sx={{ textTransform: 'none', color: 'text.secondary' }}>
                Datos SUNAT adicionales (opcional) — doc. referencia, detracción, retención, percepción
              </Button>
              <Collapse in={showAdicionales}>
                <Box sx={{ p: 1.5, mt: 0.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  {(form.tipo_documento === '07' || form.tipo_documento === '08') && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                      <Divider sx={{ width: '100%' }}><Typography variant="caption" color="text.secondary" fontWeight="bold">DOCUMENTO DE REFERENCIA</Typography></Divider>
                      <TextField select size="small" label="Tipo Doc. Ref." name="doc_ref_tipo" value={form.doc_ref_tipo}
                        onChange={handleChange} sx={{ minWidth: '14em' }}>
                        <MenuItem value=""><em>—</em></MenuItem>
                        {TIPOS_DOC_VENTA.map(t => <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>)}
                      </TextField>
                      <TextField size="small" label="Serie Ref." name="doc_ref_serie"
                        value={form.doc_ref_serie} onChange={e => setForm(f => ({ ...f, doc_ref_serie: e.target.value.toUpperCase() }))}
                        onBlur={() => autocompletarDesdeReferencia(form.doc_ref_serie, form.doc_ref_numero)}
                        sx={{ width: '9em' }} inputProps={{ style: { fontFamily: 'monospace' } }} />
                      <TextField size="small" label="Número Ref." name="doc_ref_numero" value={form.doc_ref_numero}
                        onChange={handleChange}
                        onBlur={() => autocompletarDesdeReferencia(form.doc_ref_serie, form.doc_ref_numero)}
                        sx={{ width: '10em' }} inputProps={{ style: { fontFamily: 'monospace' } }}
                        helperText="Al completar, hereda moneda y T.C. del documento original" />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <FormControlLabel control={
                      <Checkbox size="small" checked={form.tiene_detraccion}
                        onChange={e => setForm(f => ({ ...f, tiene_detraccion: e.target.checked }))} />
                    } label="Detracción SPOT" />
                    <FormControlLabel control={
                      <Checkbox size="small" checked={form.tiene_retencion}
                        onChange={e => setForm(f => ({ ...f, tiene_retencion: e.target.checked }))} />
                    } label="Retención" />
                    <FormControlLabel control={
                      <Checkbox size="small" checked={form.tiene_percepcion}
                        onChange={e => setForm(f => ({ ...f, tiene_percepcion: e.target.checked }))} />
                    } label="Percepción" />
                  </Box>

                  {form.tiene_detraccion && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                      <TextField label="Fecha SPOT" type="date" size="small" value={form.det_fecha}
                        InputLabelProps={{ shrink: true }} onChange={e => setForm(f => ({ ...f, det_fecha: e.target.value }))}
                        sx={{ width: '10em' }} />
                      <TextField label="N° Constancia" size="small" value={form.det_numero}
                        onChange={e => setForm(f => ({ ...f, det_numero: e.target.value }))}
                        sx={{ width: '11em' }} inputProps={{ style: { fontFamily: 'monospace' } }} />
                      <TextField label="Tasa %" type="number" size="small" value={form.det_tasa}
                        onChange={e => setForm(f => ({ ...f, det_tasa: e.target.value }))}
                        sx={{ width: '7em' }} inputProps={{ min: 0, max: 100, step: 0.01 }} />
                      <TextField label="Monto S/" type="number" size="small" value={form.det_monto}
                        onChange={e => setForm(f => ({ ...f, det_monto: e.target.value }))}
                        sx={{ width: '8em' }} inputProps={{ min: 0, step: 0.01 }} />
                      <TextField label="Tipo SPOT (tabla 54)" size="small" value={form.det_tipo}
                        onChange={e => setForm(f => ({ ...f, det_tipo: e.target.value }))}
                        sx={{ width: '11em' }} inputProps={{ maxLength: 3, style: { fontFamily: 'monospace' } }} placeholder="022" />
                    </Box>
                  )}

                  {form.tiene_retencion && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                      <TextField label="Código Tasa Ret." size="small" value={form.ret_codigo_tasa}
                        onChange={e => setForm(f => ({ ...f, ret_codigo_tasa: e.target.value }))}
                        sx={{ width: '11em' }} inputProps={{ maxLength: 3, style: { fontFamily: 'monospace' } }}
                        placeholder="01" helperText="01=3%, 02=6%" />
                      <TextField label="N° Retención" size="small" value={form.ret_numero}
                        onChange={e => setForm(f => ({ ...f, ret_numero: e.target.value }))}
                        sx={{ width: '11em' }} inputProps={{ maxLength: 20, style: { fontFamily: 'monospace' } }} />
                    </Box>
                  )}

                  {form.tiene_percepcion && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                      <TextField label="Código Tasa Per." size="small" value={form.per_codigo_tasa}
                        onChange={e => setForm(f => ({ ...f, per_codigo_tasa: e.target.value }))}
                        sx={{ width: '11em' }} inputProps={{ maxLength: 3, style: { fontFamily: 'monospace' } }}
                        placeholder="01" helperText="01=2%, 02=1%" />
                      <TextField label="N° Percepción" size="small" value={form.per_numero}
                        onChange={e => setForm(f => ({ ...f, per_numero: e.target.value }))}
                        sx={{ width: '11em' }} inputProps={{ maxLength: 20, style: { fontFamily: 'monospace' } }} />
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} startIcon={<CloseIcon />} color="error" variant="outlined">Cancelar</Button>
          <Button onClick={handleSave} startIcon={<SaveIcon />} variant="contained">Registrar y generar asiento</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Cobro ── */}
      <Dialog open={cobDialog} onClose={() => setCobDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Registrar Cobro — {cobDoc?.serie}-{cobDoc?.numero}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField size="small" label="Fecha de Cobro" type="date" value={cobForm.fecha_cancelacion}
                onChange={e => setCobForm({ ...cobForm, fecha_cancelacion: e.target.value })}
                InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: '12em' } }} />
              <TextField size="small" label="Monto cobrado" type="number" value={cobForm.monto_cancelado}
                onChange={e => setCobForm({ ...cobForm, monto_cancelado: e.target.value })}
                sx={{ width: { xs: '100%', sm: '11em' } }} />
              <TextField select size="small" label="Medio de Pago" value={cobForm.medio_pago}
                onChange={e => setCobForm({ ...cobForm, medio_pago: e.target.value })}
                sx={{ width: { xs: '100%', sm: '12em' } }}>
                {MEDIOS_PAGO.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField size="small" label="N° Documento de Pago" placeholder="Voucher / operación"
                value={cobForm.pago_referencia} onChange={e => setCobForm({ ...cobForm, pago_referencia: e.target.value })}
                sx={{ flex: 1, minWidth: '14em' }} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Autocomplete
                sx={{ flex: 1, minWidth: '22em' }}
                options={cuentasCaja}
                getOptionLabel={o => `${o.codigo} — ${o.descripcion}`}
                onChange={(_, v) => setCobForm({ ...cobForm, cuenta_cobpag_id: v?.id || '' })}
                renderInput={params => <TextField {...params} size="small" label="Cuenta Caja/Banco" placeholder="Buscar cuenta 10x..." />}
                filterOptions={(opts, { inputValue }) => opts.filter(o =>
                  o.codigo?.includes(inputValue) || o.descripcion?.toLowerCase().includes(inputValue.toLowerCase())
                )}
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField size="small" label="Glosa (opcional)" value={cobForm.glosa}
                onChange={e => setCobForm({ ...cobForm, glosa: e.target.value })} sx={{ flex: 1, minWidth: '14em' }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCobDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleCobro} startIcon={<PaymentIcon />}>Registrar cobro</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Importar Excel ── */}
      <Dialog open={importOpen} onClose={() => !importing && setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Importar Ventas desde Excel</DialogTitle>
        <DialogContent>
          {importErr && <Alert severity="error" sx={{ mb: 1.5 }}>{importErr}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Descargue la <b>Plantilla</b> (botón de arriba), complétela con sus ventas y súbala aquí.
            Cada fila debe tener "Tipo Operación" = VENTA.
          </Typography>
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={importing}>
            Seleccionar archivo
            <input type="file" hidden accept=".xlsx" onChange={e => {
              const f = e.target.files?.[0];
              if (f) { setImportFile(f); handleImportarPreview(f); }
            }} />
          </Button>
          {importFile && <Chip sx={{ ml: 1.5 }} label={importFile.name} onDelete={() => { setImportFile(null); setImportPreview(null); }} />}
          {importing && <CircularProgress size={20} sx={{ ml: 1.5 }} />}

          {importPreview && (
            <Box sx={{ mt: 2 }}>
              <Alert severity={importPreview.errores?.length ? 'warning' : 'success'} sx={{ mb: 1 }}>
                Se encontraron <b>{importPreview.total}</b> documento(s) válido(s) para importar.
                {importPreview.errores?.length > 0 && ` ${importPreview.errores.length} fila(s) con observaciones (no se importarán).`}
              </Alert>
              {importPreview.errores?.length > 0 && (
                <Box sx={{ maxHeight: 120, overflowY: 'auto', mb: 1, fontSize: '0.8rem', color: '#b26a00' }}>
                  {importPreview.errores.map((e, i) => <div key={i}>• {e}</div>)}
                </Box>
              )}
              <TableContainer sx={{ maxHeight: 260 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Serie-Número</TableCell><TableCell>Fecha</TableCell>
                      <TableCell>Cliente</TableCell><TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importPreview.docs.map((d, i) => (
                      <TableRow key={i}>
                        <TableCell>{d.serie}-{d.numero}</TableCell>
                        <TableCell>{d.fecha_emision}</TableCell>
                        <TableCell>{d.razon_social}</TableCell>
                        <TableCell align="right">{fmt(d.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importing}>Cerrar</Button>
          <Button variant="contained" disabled={!importPreview || importPreview.total === 0 || importing}
            onClick={handleImportarConfirmar} startIcon={importing ? <CircularProgress size={16} /> : <SaveIcon />}>
            Confirmar importación
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Cancelación Masiva ── */}
      <Dialog open={cancelMasivoOpen} onClose={() => !cancelandoMasivo && setCancelMasivoOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Cancelar {seleccionados.length} documento(s)</DialogTitle>
        <DialogContent>
          {cancelMasivoErr && <Alert severity="error" sx={{ mb: 1.5 }}>{cancelMasivoErr}</Alert>}
          <Typography variant="body2" sx={{ mb: 2 }}>Total a cobrar: <b>S/ {fmt(totalSeleccionado)}</b></Typography>

          <FormLabel sx={{ fontSize: '0.85rem' }}>¿Cómo desea cancelarlos?</FormLabel>
          <RadioGroup value={cancelMasivoForm.modo} onChange={e => setCancelMasivoForm({ ...cancelMasivoForm, modo: e.target.value })}>
            <FormControlLabel value="individual" control={<Radio size="small" />}
              label={`Un asiento por cada documento (${seleccionados.length} asientos)`} />
            <FormControlLabel value="agrupado" control={<Radio size="small" />} disabled={cancelMasivoForm.usar_fecha_emision}
              label="Un solo asiento agrupado para todos" />
          </RadioGroup>

          <FormControlLabel sx={{ mt: 0.5 }} control={
            <Checkbox size="small" checked={cancelMasivoForm.usar_fecha_emision}
              onChange={e => setCancelMasivoForm({
                ...cancelMasivoForm,
                usar_fecha_emision: e.target.checked,
                modo: e.target.checked ? 'individual' : cancelMasivoForm.modo,
                fecha_cancelacion: e.target.checked ? '' : cancelMasivoForm.fecha_cancelacion,
              })}
            />
          } label={<Typography variant="body2">Usar la fecha de emisión de cada comprobante como su fecha de cancelación</Typography>} />
          {cancelMasivoForm.usar_fecha_emision && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Cada documento se cancelará con su propia fecha de emisión — por eso solo aplica en modo individual.
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <TextField size="small" label="Fecha de cobro" type="date" InputLabelProps={{ shrink: true }}
                value={cancelMasivoForm.fecha_cancelacion} disabled={cancelMasivoForm.usar_fecha_emision}
                onChange={e => setCancelMasivoForm({ ...cancelMasivoForm, fecha_cancelacion: e.target.value })}
                sx={{ width: { xs: '100%', sm: '12em' } }} />
              <TextField select size="small" label="Medio de Pago" value={cancelMasivoForm.medio_pago}
                onChange={e => setCancelMasivoForm({ ...cancelMasivoForm, medio_pago: e.target.value })}
                sx={{ width: { xs: '100%', sm: '12em' } }}>
                {MEDIOS_PAGO.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField size="small" label="N° Documento de Pago" placeholder="Voucher / operación"
                value={cancelMasivoForm.pago_referencia}
                onChange={e => setCancelMasivoForm({ ...cancelMasivoForm, pago_referencia: e.target.value })}
                sx={{ flex: 1, minWidth: '14em' }} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Autocomplete
                sx={{ flex: 1, minWidth: '22em' }}
                options={cuentasCaja}
                getOptionLabel={o => `${o.codigo} — ${o.descripcion}`}
                onChange={(_, v) => setCancelMasivoForm({ ...cancelMasivoForm, cuenta_cobpag_id: v?.id || '' })}
                renderInput={params => <TextField {...params} size="small" label="Cuenta Caja/Banco" />}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelMasivoOpen(false)} disabled={cancelandoMasivo}>Cerrar</Button>
          <Button variant="contained" color="success" onClick={handleCancelarMasivo}
            startIcon={cancelandoMasivo ? <CircularProgress size={16} /> : <DoneAllIcon />} disabled={cancelandoMasivo}>
            Confirmar cancelación
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
